import { Popover, Checkbox, Button } from 'antd';

type FilterType = 'provider' | 'gender' | 'language' | 'accent';

interface FilterPopupProps {
	visible: boolean;
	onClose: () => void;
	filters: {
		provider: string[];
		gender: string[];
		language: string[];
		accent: string[];
	};
	selectedFilters: {
		provider: string;
		gender: string;
		language: string;
		accent: string;
	};
	onFilterChange: (type: FilterType, value: string) => void;
	children: React.ReactNode;
}

const FilterPopup: React.FC<FilterPopupProps> = ({
	visible,
	onClose,
	filters,
	selectedFilters,
	onFilterChange,
	children,
}) => {
	const handleCheckboxChange = (type: FilterType, value: string) => {
		onFilterChange(type, selectedFilters[type] === value ? 'All' : value);
	};

	const content = (
		<div className="bg-white rounded-md shadow-lg w-[380px]">
			{/* Header */}
			<div className="p-3 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
				<h3 className="text-lg font-bold">Filters</h3>
				<Button
					type="text"
					onClick={() => {
						onFilterChange('provider', 'All');
						onFilterChange('gender', 'All');
						onFilterChange('language', 'All');
						onFilterChange('accent', 'All');
					}}
					className="text-blue-600 font-medium"
				>
					Clear all
				</Button>
			</div>

			{/* Scrollable Content */}
			<div className="max-h-[400px] overflow-y-auto">
				<div className="py-2 px-3">
					{/* Provider */}
					<div className="py-2">
						<h4 className="text-base font-medium mb-2">Provider</h4>
						<div className="grid grid-cols-2 gap-x-2 gap-y-1">
							{filters.provider.map((value) => (
								<Checkbox
									key={value}
									checked={selectedFilters.provider === value}
									onChange={() => handleCheckboxChange('provider', value)}
									className="ant-checkbox-wrapper-custom"
								>
									<span className="text-sm">{value}</span>
								</Checkbox>
							))}
						</div>
					</div>

					{/* Language */}
					<div className="border-t border-gray-100 py-2 mt-2">
						<h4 className="text-base font-medium mb-2 mt-2">Language</h4>
						<div className="grid grid-cols-2 gap-x-2 gap-y-1">
							{filters.language.map((value) => (
								<Checkbox
									key={value}
									checked={selectedFilters.language === value}
									onChange={() => handleCheckboxChange('language', value)}
									className="ant-checkbox-wrapper-custom"
								>
									<span className="text-sm">{value}</span>
								</Checkbox>
							))}
						</div>
					</div>

					{/* Gender */}
					<div className="border-t border-gray-100 py-2 mt-2">
						<h4 className="text-base font-medium mb-2 mt-2">Gender</h4>
						<div className="grid grid-cols-2 gap-x-2 gap-y-1">
							{filters.gender.map((value) => (
								<Checkbox
									key={value}
									checked={selectedFilters.gender === value}
									onChange={() => handleCheckboxChange('gender', value)}
									className="ant-checkbox-wrapper-custom"
								>
									<span className="text-sm">{value}</span>
								</Checkbox>
							))}
						</div>
					</div>

					{/* Accent */}
					<div className="border-t border-gray-100 py-2 mt-2">
						<h4 className="text-base font-medium mb-2 mt-2">Accent</h4>
						<div className="grid grid-cols-2 gap-x-2 gap-y-1">
							{filters.accent.map((value) => (
								<Checkbox
									key={value}
									checked={selectedFilters.accent === value}
									onChange={() => handleCheckboxChange('accent', value)}
									className="ant-checkbox-wrapper-custom"
								>
									<span className="text-sm">{value}</span>
								</Checkbox>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* Footer */}
			<div className="p-3 bg-gray-50 border-t border-gray-200 flex justify-end items-center sticky bottom-0 z-10">
				<span className="text-sm text-gray-500">122 voices</span>
			</div>
		</div>
	);

	return (
		<Popover
			open={visible}
			onOpenChange={(visible) => !visible && onClose()}
			content={content}
			trigger="click"
			placement="bottomRight"
			align={{
				offset: [-20, 10],
			}}
			overlayInnerStyle={{ padding: 0 }}
			arrow={false}
			getPopupContainer={(trigger) => trigger.parentElement || document.body}
		>
			{children}
		</Popover>
	);
};

export default FilterPopup;
